// eslint-disable-next-line import/no-cycle
import type { eventWithTime } from "@rrweb/types";
import type { Fields } from "@vuex-orm/core";
import type InheritanceTypes from "@vuex-orm/core/dist/src/model/contracts/InheritanceTypes";
import { Action, Click, Input, Keypress, BrowserPlayback, Scroll } from "./ActionHierarchy";
import type { BrowserActionJson } from "./ActionHierarchy";
import type { ScreenshotFileKeys } from "./ActionTypes";

type eventWithTimeAndData = eventWithTime & { data?: { [key: string]: any } };

export class BrowserAction extends Action implements ScreenshotFileKeys, BrowserActionJson {
  // This is the name used as module name of the Vuex Store.
  public static entity = "browserActions";

  public static baseEntity = "actions";

  public static types(): InheritanceTypes {
    return {
      Click,
      Input,
      Scroll,
      Keypress,
      BrowserPlayback,
    };
  }

  public static fields(): Fields {
    return {
      ...super.fields(),
      events: this.attr([]),
      stale: this.boolean(false), // Formerly: staleAction
      previewable: this.boolean(false),
      elementVisible: this.boolean(true),
      // element: this.attr(), -> this can possibly not be stored here because it’s a DOM element
      elementOuterHtml: this.string(""), // previously: htmlElement -> should be populated with element.outerHtml
      closeupScreenshotFileKey: this.string(null).nullable(),
      fullScreenshotFileKey: this.string(null).nullable(),
      captureMetaPartition: this.string("persist:user-recording"),
      captureMetaHref: this.string(null).nullable(),
      captureMetaTargetId: this.number(-1),
      startsAt: this.number(null).nullable(),
      endsAt: this.number(null).nullable(),
    };
  }

  public declare events: eventWithTimeAndData[];

  public declare stale: boolean;

  public declare previewable: boolean;

  public declare elementVisible: boolean;

  public declare elementOuterHtml: string;

  public declare closeupScreenshotFileKey: string | null;

  public declare fullScreenshotFileKey: string | null;

  public declare captureMetaPartition: string;

  public declare captureMetaHref: string | null;

  public declare captureMetaTargetId: number;

  public declare startsAt: number | null;

  public declare endsAt: number | null;

  public get elementPreview(): null | Element {
    // TODO move to TimelineInput component
    if (this.elementOuterHtml) {
      const container = document.createElement("template");
      container.innerHTML = this.elementOuterHtml;
      const elementPreview = container.content.firstElementChild;
      elementPreview?.removeAttribute("id");
      elementPreview?.removeAttribute("class");
      // Make the element unreachable via sequential keyboard navigation with a tabIndex of -1:
      elementPreview?.setAttribute("tabIndex", "-1");
      return elementPreview;
    }
    return null;
  }

  public get position(): { top: number; left: number } | undefined {
    if (!this.events) return undefined;
    const firstEventWithAPosition = this.events.find(
      (e) => e.data && "x" in e.data && "y" in e.data,
    );
    if (!firstEventWithAPosition) return undefined;
    const { x, y } = firstEventWithAPosition.data as { x: number; y: number };
    return { top: y, left: x };
  }

  // eslint-disable-next-line class-methods-use-this
  public get isPassword(): boolean {
    return false;
  }

  public readonly isBrowserAction = true;

  public get initialPlayableTimestamp(): number | null {
    // This method returns the timestamp that the rrweb-player should display by default.
    return this.startsAt;
  }

  public get recordedDuration(): number | undefined {
    if (this.startsAt && this.endsAt) {
      return (this.endsAt - this.startsAt) / 1000;
    }
    return undefined;
  }

  public markStale(): void {
    this.$update({ stale: true });
  }

  public static deleteStale(): void {
    this.query()
      .where("stale", true)
      .all()
      .forEach((action) => action.$delete);
  }

  private cachedElement: Element | null = null;

  public get element(): Element | null {
    if (!this.elementOuterHtml) return null;
    if (this.cachedElement) return this.cachedElement;
    const element = document.createElement("template");
    element.innerHTML = this.elementOuterHtml;
    this.cachedElement = element.content.firstElementChild;
    return this.cachedElement;
  }

  // public get elementType(): string | null {
  //   if (!this.element) return null;
  //   return this.element.tagName.toLowerCase();
  // }

  public get textFieldType(): string | null {
    if (this.element instanceof HTMLTextAreaElement) return "text";
    if (!(this.element instanceof HTMLInputElement)) return null;
    const inputElement = this.element as HTMLInputElement;
    if (["text", "email", "password", "number", "search"].includes(inputElement.type)) {
      return this.element.type;
    }
    if (inputElement.type === "tel") {
      return "phone number";
    }
    if (inputElement.type === "url") {
      return "URL";
    }
    return "text";
    // if (!inputElement.type) {
    //   return "text";
    // }
    // return null;
  }

  public get isTextField(): boolean {
    return this.textFieldType !== null;
  }

  public get isButton(): boolean {
    if (this.element instanceof HTMLButtonElement) return true;
    return this.element instanceof HTMLInputElement && this.element.type === "submit";
  }

  public get isRadio(): boolean {
    return this.element instanceof HTMLInputElement && this.element.type === "radio";
  }

  public get isCheckbox(): boolean {
    return this.element instanceof HTMLInputElement && this.element.type === "checkbox";
  }

  public get elementCategory(): string | null {
    if (this.isTextField) {
      return "text_field";
    }
    if (this.isButton) {
      return "button";
    }
    if (this.isRadio) {
      return "radio";
    }
    if (this.isCheckbox) {
      return "checkbox";
    }
    return null;
  }

  public get isUnknownCategory(): boolean {
    return this.elementCategory === null;
  }

  public get elementDescription(): string | null {
    if (!this.element) return null;
    if (this.element instanceof HTMLTextAreaElement || this.element instanceof HTMLInputElement) {
      if (this.element.placeholder) {
        return `“${this.element.placeholder}” field`;
      }
      return `${this.textFieldType} field`.trim();
    }
    if (this.element instanceof HTMLButtonElement) {
      const text = this.element.innerText ? `“${this.element.innerText}”` : "";
      return `${text} button`.trim();
    }
    if (this.isButton && this.element instanceof HTMLInputElement) {
      const text = this.element.value ? `“${this.element.value}”` : "";
      return `${text} button`.trim();
    }
    if (this.isRadio) {
      return "radio button";
    }
    if (this.isCheckbox) {
      return "checkbox";
    }
    if (this.element instanceof HTMLTextAreaElement || this.element instanceof HTMLInputElement) {
      return this.element.placeholder;
    }
    return null;
  }
}
